import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling, ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';

/**
 * ContentDNA Service - Manages organization and brand-level content DNA
 * Implements role-based access control for DNA insights
 */
export class ContentDNAService {
  private static readonly logger = logger.child({ service: 'ContentDNAService' });

  /**
   * Get organization-level ContentDNA with role-based field filtering
   * Admins/Owners: Full DNA insights
   * Editors/Viewers: Only updated_at timestamp
   */
  static async getOrganizationDNA(organizationId: string, userRole: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching organization DNA', { organizationId, userRole });

      const { data, error } = await supabase
        .from('organization_content_dna')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;

      if (!data) return null;

      // Role-based field filtering
      const isAdmin = userRole === 'owner' || userRole === 'admin';
      
      this.logger.info('Organization DNA fetched', { 
        organizationId, 
        userRole, 
        fullAccess: isAdmin 
      });

      if (isAdmin) {
        return data; // Full DNA for admins
      } else {
        // Only timestamp for non-admins
        return {
          id: data.id,
          organization_id: data.organization_id,
          updated_at: data.updated_at,
        };
      }
    }, { operation: 'getOrganizationDNA', table: 'organization_content_dna' });
  }

  /**
   * Analyze organization content from URL
   * Admin-only operation
   */
  static async analyzeOrganizationURL(organizationId: string, contentUrl: string) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Analyzing organization URL', { organizationId, contentUrl });

      const { data, error } = await supabase.functions.invoke('analyze-content-dna', {
        body: {
          organizationId,
          contentUrl,
          level: 'organization',
        },
      });

      if (error) throw ServiceError.internal('Failed to analyze organization content', { originalError: error });

      this.logger.info('Organization URL analyzed', { organizationId });

      return data;
    }, { operation: 'analyzeOrganizationURL' });
  }

  /**
   * Get organization DNA for generation
   */
  static async getOrganizationDNAForGeneration(organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching DNA for generation', { organizationId });

      const { data, error } = await supabase
        .from('organization_content_dna')
        .select('id, organization_id, brand_voice, key_topics, lexicon, tone_analysis, rhetorical_devices, updated_at')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;

      this.logger.info('DNA for generation fetched', { organizationId, found: !!data });

      return data;
    }, { operation: 'getOrganizationDNAForGeneration', table: 'organization_content_dna' });
  }

  /**
   * Alias for backward compatibility
   */
  static async getBrandDNAForGeneration(organizationId: string) {
    return this.getOrganizationDNAForGeneration(organizationId);
  }

  /**
   * Check brand DNA status (stub for MVP - checks organization DNA)
   */
  static async checkBrandDNAStatus(organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Checking DNA status', { organizationId });

      const { data, error } = await supabase
        .from('organization_content_dna')
        .select('id, brand_voice, key_topics, updated_at')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (error) throw error;

      const hasVoice = !!(data?.brand_voice);
      const hasTopics = Array.isArray(data?.key_topics) && data.key_topics.length > 0;
      
      const status = {
        exists: !!data,
        hasVoice,
        hasTopics,
        isComplete: hasVoice && hasTopics,
        lastUpdated: data?.updated_at || null,
      };

      this.logger.info('DNA status checked', { organizationId, ...status });

      return status;
    }, { operation: 'checkBrandDNAStatus', table: 'organization_content_dna' });
  }

  /**
   * Analyze brand content (uses organization level for MVP)
   */
  static async analyzeBrandURL(
    organizationId: string, 
    contentUrl: string
  ) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Analyzing brand URL', { organizationId, contentUrl });

      const { data, error } = await supabase.functions.invoke('analyze-content-dna', {
        body: {
          organizationId,
          contentUrl,
          level: 'organization',
        },
      });

      if (error) throw ServiceError.internal('Failed to analyze organization content', { originalError: error });

      this.logger.info('Brand URL analyzed', { organizationId });

      return data;
    }, { operation: 'analyzeBrandURL' });
  }

  /**
   * Get content sources for organization
   */
  static async getOrganizationSources(organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching content sources', { organizationId });

      const { data, error } = await supabase
        .from('content_sources')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      this.logger.info('Content sources fetched', { 
        organizationId, 
        count: data?.length || 0 
      });

      return data || [];
    }, { operation: 'getOrganizationSources', table: 'content_sources' });
  }

  /**
   * Add content source URL
   * Admin-only operation
   */
  static async addContentSource(params: {
    organizationId: string;
    url: string;
    type: 'website' | 'youtube' | 'podcast' | 'pdf' | 'manual';
    title?: string;
  }) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Adding content source', { 
        organizationId: params.organizationId,
        type: params.type 
      });

      const insertData = {
        organization_id: params.organizationId,
        url: params.url,
        type: params.type,
        title: params.title,
      };

      const { data, error } = await supabase
        .from('content_sources')
        .insert(insertData)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('No data returned from insert');

      this.logger.info('Content source added', { sourceId: data.id });

      return data;
    }, { operation: 'addContentSource', table: 'content_sources' });
  }

  /**
   * Update user overrides for specific DNA fields
   * Only allows overriding: tone_analysis, jargon_blacklist, lexicon
   */
  static async updateUserOverrides(
    organizationId: string,
    overrides: {
      tone_analysis?: Record<string, number>;
      jargon_blacklist?: string[];
      lexicon?: string[];
    }
  ) {
    return withServiceErrorHandling(async () => {
      // Validate override keys
      const allowedKeys = ['tone_analysis', 'jargon_blacklist', 'lexicon'];
      const invalidKeys = Object.keys(overrides).filter(k => !allowedKeys.includes(k));
      
      if (invalidKeys.length > 0) {
        throw ServiceError.validation(`Cannot override fields: ${invalidKeys.join(', ')}`);
      }

      this.logger.info('Updating user overrides', { organizationId });

      // Merge with existing overrides
      const { data: existing } = await supabase
        .from('organization_content_dna')
        .select('user_overrides')
        .eq('organization_id', organizationId)
        .maybeSingle();

      const existingOverrides = (existing?.user_overrides as Record<string, unknown>) || {};
      const merged = { ...existingOverrides, ...overrides };

      const { data, error } = await supabase
        .from('organization_content_dna')
        .update({ user_overrides: merged })
        .eq('organization_id', organizationId)
        .select()
        .maybeSingle();

      if (error) throw error;

      this.logger.info('User overrides updated', { organizationId });

      return data;
    }, { operation: 'updateUserOverrides', table: 'organization_content_dna' });
  }

  /**
   * Reset/clear all organization DNA data for rescan
   * Deletes organization_content_dna row and all content_sources
   * Admin-only operation
   */
  static async resetOrganizationDNA(organizationId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Resetting organization DNA', { organizationId });

      // Delete organization_content_dna row
      const { error: dnaError } = await supabase
        .from('organization_content_dna')
        .delete()
        .eq('organization_id', organizationId);

      if (dnaError) {
        this.logger.warn('Failed to delete organization DNA', { error: dnaError.message });
      }

      // Delete all content_sources for this organization
      const { error: sourcesError } = await supabase
        .from('content_sources')
        .delete()
        .eq('organization_id', organizationId);

      if (sourcesError) {
        this.logger.warn('Failed to delete content sources', { error: sourcesError.message });
      }

      // Delete content_excerpts for this organization
      const { error: excerptsError } = await supabase
        .from('content_excerpts')
        .delete()
        .eq('organization_id', organizationId);

      if (excerptsError) {
        this.logger.warn('Failed to delete content excerpts', { error: excerptsError.message });
      }

      this.logger.info('Organization DNA reset complete', { organizationId });

      return { success: true };
    }, { operation: 'resetOrganizationDNA' });
  }
}
